<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useContentStore } from '@/stores/content';
import type { PageDetailRow } from '@/db/content';
import { usePageHead, type PageHeadPayload } from '@/composables/usePageHead';
import { normalizeCmsPlainText, normalizeWagtailRichTextHtml, stripHtmlToText } from '@/utils/html';
import type { ApiImage } from '@/types/images';
import HomePage from '@/components/HomePage.vue';
import NewsIndexPage from '@/components/NewsIndexPage.vue';
import ProgramsIndexPage from '@/components/ProgramsIndexPage.vue';
import TrainingCentersIndexPage from '@/components/TrainingCentersIndexPage.vue';

/** Lazy: large components + pull Dexie training-centre helpers off the default route chunk. */
const NewsArticlePage = defineAsyncComponent(() =>
  import('@/components/NewsArticlePage.vue')
);
const TrainingCentreDetailPage = defineAsyncComponent(() =>
  import('@/components/TrainingCentreDetailPage.vue')
);
const ProgramDetailPage = defineAsyncComponent(() =>
  import('@/components/ProgramDetailPage.vue')
);
import PageHeader from '@/components/PageHeader.vue';
import PageHeaderContent from '@/components/PageHeaderContent.vue';
import PageHeaderQuickLinks from '@/components/PageHeaderQuickLinks.vue';
import PartnersCarousel from '@/components/PartnersCarousel.vue';
import type { PageHeaderReadMore } from '@/components/PageHeader.vue';
import type { SiteLogo } from '@/navTree';

/** Map Wagtail page meta.type to the component that renders it. */
const PAGE_TYPE_COMPONENTS: Record<string, import('vue').Component> = {
  'home.HomePage': HomePage,
  'home.NormalPage': HomePage,
  'home.NewsIndexPage': NewsIndexPage,
  'home.NewsArticlePage': NewsArticlePage,
  'home.ProgramsIndexPage': ProgramsIndexPage,
  'home.TrainingCentersIndexPage': TrainingCentersIndexPage,
  'home.TrainingCenterDetailPage': TrainingCentreDetailPage,
  'home.ProgramDetailPage': ProgramDetailPage,
};

function componentForPage(page: PageDetailRow | null | undefined): import('vue').Component {
  const type = (page?.meta as { type?: string } | undefined)?.type;
  return (type ? PAGE_TYPE_COMPONENTS[type] : undefined) ?? HomePage;
}

const contentStore = useContentStore();
const { t } = useI18n();

const isPreviewMode = computed(() => contentStore.isCurrentPreviewMode);
const currentPayload = computed(() => contentStore.getCurrentPagePayload);
const pageLoading = computed(() =>
  isPreviewMode.value ? contentStore.currentPreview.loading : contentStore.currentPageLoading
);
const pageError = computed(() =>
  isPreviewMode.value
    ? (contentStore.currentPreview.error ? 'Preview could not be loaded.' : null)
    : contentStore.currentPageError
);
const notFound = computed(() => !isPreviewMode.value && contentStore.isCurrentPageNotFound);

/** Head payload for title and meta: prefer detail/preview when available, else listing item for early meta. */
const headPayload = computed<PageHeadPayload | null>(() => {
  if (currentPayload.value) {
    const p = currentPayload.value as PageHeadPayload & { rich_title?: string | null };
    return {
      title: normalizeCmsPlainText(p.title),
      rich_title: p.rich_title,
      meta: p.meta,
    };
  }
  if (notFound.value) return { title: 'Page not found' };
  return null;
});

usePageHead(headPayload);

/** The page payload to pass to the page component (preview or normal detail). */
const pagePayload = computed((): PageDetailRow | undefined => {
  const payload = currentPayload.value;
  return payload && payload !== null ? payload : undefined;
});

const pageSearchDescription = computed(() => {
  const meta = pagePayload.value?.meta as { search_description?: string } | undefined;
  const raw = meta?.search_description;
  return typeof raw === 'string' ? normalizeCmsPlainText(raw) : raw;
});

/** Page detail image (promote tab); home featured band and content-style headers use it when set. */
const pageImage = computed(() => pagePayload.value?.image as ApiImage | undefined | null);

const pageMetaType = computed(
  () => (pagePayload.value?.meta as { type?: string } | undefined)?.type
);

/** Page types that ship their own hero/title; skip duplicate Wagtail promote header. */
const PAGE_TYPES_WITHOUT_GLOBAL_HEADER = new Set([
  'home.NewsArticlePage',
  'home.TrainingCenterDetailPage',
  'home.ProgramDetailPage',
]);

/** Hide PageView header strip when the page component already owns the main heading. */
const showPageHeaderRegion = computed(() => {
  if (!pagePayload.value) return false;
  const ty = pageMetaType.value;
  if (!ty) return true;
  return !PAGE_TYPES_WITHOUT_GLOBAL_HEADER.has(ty);
});

/** NormalPage with API header_style "content" uses PageHeaderContent, not PageHeader. */
const isNormalPageContentStyle = computed(() => {
  if (pageMetaType.value !== 'home.NormalPage') return false;
  const raw = (pagePayload.value as { header_style?: string | null } | undefined)?.header_style;
  return raw === 'content';
});

/** HomePage always uses the featured (full-bleed) band; other PageHeader uses use minimal. */
const pageHeaderVariant = computed((): 'minimal' | 'featured' => {
  if (pageMetaType.value === 'home.HomePage') return 'featured';
  return 'minimal';
});

const pageImageAlt = computed((): string => {
  const img = pageImage.value;
  const rich = pagePayload.value?.rich_title;
  const plainFromRich = typeof rich === 'string' ? stripHtmlToText(rich) : '';
  const raw = img?.title ?? (plainFromRich || pagePayload.value?.title);
  return typeof raw === 'string' ? raw : '';
});

/** Visible page heading: rich HTML when set, else plain title. */
const visibleTitleHtml = computed((): string | null => {
  const raw = pagePayload.value?.rich_title;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  return normalizeWagtailRichTextHtml(raw);
});

const pageComponent = computed(() => componentForPage(pagePayload.value));

const plainPageTitle = computed((): string => {
  const t = pagePayload.value?.title;
  return typeof t === 'string' ? normalizeCmsPlainText(t) : '';
});

/** HomePage read more in-page link when label and anchor are set in Wagtail. */
const pageReadMore = computed((): PageHeaderReadMore | null => {
  const p = pagePayload.value;
  if (!p) return null;
  const text = p.read_more_text;
  const anchor = p.read_more_anchor;
  if (typeof text !== 'string' || !text.trim()) return null;
  if (typeof anchor !== 'string' || !anchor.trim()) return null;
  return { text: text.trim(), href: `#${anchor.trim()}` };
});

const pageQuickLinks = computed(() => {
  const p = pagePayload.value as { page_links?: { pages?: { page_id?: number }[] } | null } | undefined;
  return p?.page_links ?? null;
});

/** HomePage only: partner logos for carousel above site footer (API `partners`). */
const homePartnersLogos = computed((): SiteLogo[] => {
  const raw = (pagePayload.value as { partners?: SiteLogo[] } | undefined)?.partners;
  return Array.isArray(raw) ? raw : [];
});

const showHomePartnersCarousel = computed(
  () => pageMetaType.value === 'home.HomePage' && homePartnersLogos.value.length > 0
);

/** When there is no Wagtail promote/hero image, the first main block may supply LCP. */
const prioritizeFirstBlockLcp = computed(() => {
  const t = pageMetaType.value;
  if (t !== 'home.HomePage' && t !== 'home.NormalPage') return false;
  return !pageImage.value;
});

/** Only HomePage reads `prioritizeFirstBlockLcp` — avoid passing to other page components. */
const homePageStreamProps = computed(() => {
  const t = pageMetaType.value;
  if (t !== 'home.HomePage' && t !== 'home.NormalPage') return {};
  return { prioritizeFirstBlockLcp: prioritizeFirstBlockLcp.value };
});

/** Listing-only shell offline (§8): minimal copy in the body region; full StreamField needs sync when online. */
const showListingShellNotice = computed(
  () => !isPreviewMode.value && contentStore.isCurrentPageShellOnly
);
</script>

<template>
  <!-- Headless preview: draft content from Wagtail preview API -->
  <template v-if="isPreviewMode">
    <div v-if="pageLoading" class="not-found">Loading preview…</div>
    <div v-else-if="pageError" class="not-found">
      <p>Preview could not be loaded.</p>
    </div>
    <template v-else-if="pagePayload">
      <div class="preview-banner" role="status">Preview mode — draft content</div>
      <div
        v-if="showPageHeaderRegion"
        class="page-view__header-group"
        :class="{ 'page-view__header-group--content-style': isNormalPageContentStyle }"
      >
        <template v-if="isNormalPageContentStyle">
          <PageHeaderContent
            :plain-title="plainPageTitle"
            :title-html="visibleTitleHtml"
            :description="pageSearchDescription"
            :image="pageImage ?? null"
            :image-alt="pageImageAlt"
            :read-more="pageReadMore"
          />
          <PageHeaderQuickLinks :page-links="pageQuickLinks" variant="minimal" />
        </template>
        <template v-else>
          <PageHeader
            :variant="pageHeaderVariant"
            :plain-title="plainPageTitle"
            :title-html="visibleTitleHtml"
            :description="pageSearchDescription"
            :image="pageImage ?? null"
            :image-alt="pageImageAlt"
            :read-more="pageReadMore"
          />
          <PageHeaderQuickLinks :page-links="pageQuickLinks" :variant="pageHeaderVariant" />
        </template>
      </div>
      <component
        :is="pageComponent"
        :page="pagePayload"
        v-bind="homePageStreamProps"
      />
      <PartnersCarousel
        v-if="showHomePartnersCarousel"
        :partners="homePartnersLogos"
      />
    </template>
  </template>
  <!-- Normal path-based page -->
  <template v-else>
    <div
      v-if="pagePayload && showPageHeaderRegion"
      class="page-view__header-group"
      :class="{ 'page-view__header-group--content-style': isNormalPageContentStyle }"
    >
      <template v-if="isNormalPageContentStyle">
        <PageHeaderContent
          :plain-title="plainPageTitle"
          :title-html="visibleTitleHtml"
          :description="pageSearchDescription"
          :image="pageImage ?? null"
          :image-alt="pageImageAlt"
          :read-more="pageReadMore"
        />
        <PageHeaderQuickLinks :page-links="pageQuickLinks" variant="minimal" />
      </template>
      <template v-else>
        <PageHeader
          :variant="pageHeaderVariant"
          :plain-title="plainPageTitle"
          :title-html="visibleTitleHtml"
          :description="pageSearchDescription"
          :image="pageImage ?? null"
          :image-alt="pageImageAlt"
          :read-more="pageReadMore"
        />
        <PageHeaderQuickLinks :page-links="pageQuickLinks" :variant="pageHeaderVariant" />
      </template>
    </div>

    <template v-if="pagePayload">
      <p
        v-if="showListingShellNotice"
        class="page-view__connection-notice"
        role="status"
      >
        {{ t('common.connectionRequired') }}
      </p>
      <component
        :is="pageComponent"
        :page="pagePayload"
        v-bind="homePageStreamProps"
      />
      <PartnersCarousel
        v-if="showHomePartnersCarousel"
        :partners="homePartnersLogos"
      />
    </template>
    <div v-else-if="pageLoading" class="not-found">Loading…</div>
    <div v-else-if="pageError" class="not-found">
      <p>{{ pageError }}</p>
    </div>
    <div v-else-if="notFound" class="not-found">
      <h1>Page not found</h1>
      <p>The page you requested could not be found.</p>
    </div>
    <div v-else class="not-found">
      <p>Loading…</p>
    </div>
  </template>
</template>

<style scoped>
/*
 * Default: gap below header + quick links. Prototype PageHero pages (NormalPage header_style
 * content): no margin under the hero — spacing is the next wrapper’s py-0 sm:py-6 lg:py-12 only.
 */
.page-view__header-group {
  margin-bottom: 6.25rem;
}
.page-view__header-group--content-style {
  margin-bottom: 0;
}

.not-found {
  padding: 2rem;
}
.preview-banner {
  padding: 0.5rem 1rem;
  background: var(--color-preview-banner, #f0f7ff);
  border-bottom: 1px solid var(--color-preview-border, #b3d4ff);
  font-size: 0.875rem;
}

.page-view__connection-notice {
  margin: 0 0 1.25rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: var(--color-text-muted, #555);
  background: var(--color-surface-muted, #f4f4f5);
  border-radius: 0.375rem;
  text-align: center;
}
</style>
